import { hslArrayToCSS } from '@/src/renderer/lib/colorUtils'
import { getRewardById, getRewardIconUrl } from '@/src/renderer/lib/data/rewardCatalog'
import { SQUIRCLE_PATH } from './squircle'
import type { RewardCatalogIconProps } from './RewardCatalogIcon.types'
import './RewardCatalogIcon.scss'

// Fallback grey for a reward whose colour didn't resolve.
const FALLBACK_COLOR = 'hsl(0, 0%, 85%)'

/**
 * Icon for a reward from the **bundled** catalogue: squircle background in the
 * reward's colour, emoji artwork on top.
 *
 * Resolves everything locally — no network, no auth — per ADR 0003. Renders
 * nothing if the id is unknown.
 *
 * @example
 * <RewardCatalogIcon rewardId={task.rewardId} name={rewardName(reward, 'de')} size={32} />
 */
export const RewardCatalogIcon = ({ rewardId, name, size = 32, className }: RewardCatalogIconProps) => {
  const reward = getRewardById(rewardId)
  // Pick the smallest bundled variant that survives a 2x display, the same way
  // `pickCdnSize` does: at 32px (picker) 64 is exact, at 40px (focus pill) it is
  // already short.
  const iconUrl = getRewardIconUrl(rewardId, size > 32 ? 256 : 64)
  if (!reward) return null

  const backgroundColor = reward.hsl ? hslArrayToCSS(reward.hsl) : FALLBACK_COLOR
  // The artwork sits at 60% of the squircle.
  const artworkSize = Math.round(size * 0.6)

  return (
    <span
      className={`reward-catalog-icon${className ? ` ${className}` : ''}`}
      style={{ width: size, height: size }}
    >
      <svg
        className="reward-catalog-icon__background"
        width={size}
        height={size}
        viewBox="0 0 40 40"
        fill="none"
        aria-hidden="true"
      >
        <path d={SQUIRCLE_PATH} fill={backgroundColor} />
      </svg>
      {iconUrl && (
        <img
          className="reward-catalog-icon__image"
          src={iconUrl}
          alt={name}
          width={artworkSize}
          height={artworkSize}
        />
      )}
    </span>
  )
}
